import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { useGetStocksQuery, useGetStockLogsQuery } from "../../../api/endpoints/dealerStockReport";
import { useGetParentWarehousesQuery } from "../../../api/endpoints/dealerWarehouse";
import { useGetUserParamsQuery } from "../../../api/endpoints/user";
import { useSelector } from "react-redux";
import { selectUserParams } from "../../../store/slices/userParamsSlice";
import { useLanguage } from "../../../contexts/LanguageContext";

const codeToLangId = (code) =>
  ({ TR: 1, EN: 2, RO: 3, IT: 4, FR: 5, DE: 6 }[String(code).toUpperCase()] ?? 2);

const useDealerStockReportData = () => {
  const { th, currentLanguageId, language, getCurrentLanguageId } = useLanguage();

  // user params (branch vs.)
  const { data: userParamsResp } = useGetUserParamsQuery();
  const userParamsFromStore = useSelector(selectUserParams);
  const userParams = userParamsFromStore || userParamsResp || {};
  const appBranchId = userParams?.dealerId ?? 0;

  const languageId =
    typeof currentLanguageId === "number" ? currentLanguageId : codeToLangId(language);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });

  const filterFieldNames = useMemo(() => ["search", "warehouseId"], []);
  const createInitialFilters = useCallback(() => {
    const f = {};
    for (const field of filterFieldNames) {
      if (field === "search")
        f[field] = {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }]
        };
      else
        f[field] = { value: null, matchMode: FilterMatchMode.EQUALS };
    }
    f.global = { value: null, matchMode: FilterMatchMode.CONTAINS };
    return f;
  }, [filterFieldNames]);
  const [filters, setFilters] = useState(createInitialFilters());

  const { data: parentWarehouses = [] } = useGetParentWarehousesQuery(
    { appBranchId, languageId, pageNumber: 1, pageSize: 1000, isActive: 1 },
    { skip: !appBranchId || !languageId, refetchOnMountOrArgChange: false }
  );
  const warehouseOptions = useMemo(() => {
    const arr = parentWarehouses?.data || parentWarehouses || [];
    return arr.map((x) => ({ label: x.warehouseName, value: x.id }));
  }, [parentWarehouses]);

  const queryParams = useMemo(
    () => ({
      languageId,
      pageNumber: pagination.PageNumber,
      pageSize: pagination.PageSize,
      search: filters.search?.constraints?.[0]?.value || "",
      warehouseId: filters.warehouseId?.value ?? null
    }),
    [languageId, pagination, filters]
  );

  const { data: listData, isLoading, isFetching, refetch } = useGetStocksQuery(queryParams, {
    skip: !languageId,
    refetchOnMountOrArgChange: true
  });

  useEffect(() => {
    const paged = listData?.rows
      ? listData
      : listData?.data?.rows
        ? listData.data
        : { rows: [], totalCount: 0, pageItemCount: 0 };

    setRows(Array.isArray(paged.rows) ? paged.rows : []);
    setTotalCount(paged.totalCount || 0);
  }, [listData]);

  const loading = isLoading || isFetching || !languageId;

  const handleFilterChange = useCallback(
    (updated) => {
      setFilters((prev) => {
        const next = { ...prev };

        if ("search" in updated) {
          next.search = {
            operator: FilterOperator.AND,
            constraints: [
              { value: updated.search ?? null, matchMode: FilterMatchMode.CONTAINS }
            ]
          };
        }
        if ("warehouseId" in updated) {
          next.warehouseId = { value: updated.warehouseId ?? null, matchMode: FilterMatchMode.EQUALS };
        }

        return next;
      });
      setPagination((p) => ({ ...p, PageNumber: 1 }));
      setTimeout(() => refetch(), 50);
    },
    [refetch]
  );

  const handleApplyFilters = useCallback(
    (updated) => handleFilterChange(updated),
    [handleFilterChange]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setPagination((p) => ({ ...p, PageNumber: 1 }));
    setTimeout(() => refetch(), 50);
  }, [createInitialFilters, refetch]);

  const handleSearch = useCallback(
    (text) => handleFilterChange({ search: text || "" }),
    [handleFilterChange]
  );

  const onPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination((prev) => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows
    }));
  }, []);

  const [logDialogVisible, setLogDialogVisible] = useState(false);
  const [selectedPartCode, setSelectedPartCode] = useState(null);
  const [logPagination, setLogPagination] = useState({ PageNumber: 1, PageSize: 10 });

  const logParams = useMemo(
    () =>
      selectedPartCode
        ? {
          partCode: selectedPartCode,
          pageNumber: logPagination.PageNumber,
          pageSize: logPagination.PageSize,
          languageId
        }
        : null,
    [selectedPartCode, logPagination, languageId]
  );

  const {
    data: logData,
    isLoading: isLogLoading,
    isFetching: isLogFetching,
    refetch: refetchLogs
  } = useGetStockLogsQuery(logParams, { skip: !logParams });

  const [logRows, setLogRows] = useState([]);
  const [logTotalCount, setLogTotalCount] = useState(0);

  useEffect(() => {
    const paged = logData?.rows
      ? logData
      : logData?.data?.rows
        ? logData.data
        : { rows: [], totalCount: 0, pageItemCount: 0 };

    setLogRows(Array.isArray(paged.rows) ? paged.rows : []);
    setLogTotalCount(paged.totalCount || 0);
  }, [logData]);

  const openLogDialog = useCallback((partCode) => {
    setSelectedPartCode(partCode);
    setLogPagination({ PageNumber: 1, PageSize: 10 });
    setLogDialogVisible(true);
    setTimeout(() => refetchLogs(), 0);
  }, [refetchLogs]);

  const closeLogDialog = useCallback(() => {
    setLogDialogVisible(false);
    setSelectedPartCode(null);
  }, []);

  const onLogPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setLogPagination((prev) => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows
    }));
  }, []);

  const filterFields = useMemo(
    () => [
      {
        field: "search",
        headerName: th("dealerStockReport.searchLabel") || "Parça Kodu / Açıklama",
        type: "text"
      },
      {
        field: "warehouseId",
        headerName: th("dealerStockReport.warehouse") || "Ambar",
        type: "select",
        options: warehouseOptions,
         filterPlaceholder: th("dealerWarehouse.warehouseNamePlaceholder") || "Ambar seçiniz"
      }
    ],
    [th, warehouseOptions]
  );

  const customFilterTypes = useMemo(
    () => ({ search: "text", warehouseId: "select" }),
    []
  );

  const customFilterOptions = useMemo(() => ({}), []);

  const exportParams = useMemo(
  () => ({
    search: filters.search?.constraints?.[0]?.value || null,
    warehouseId: filters.warehouseId?.value ?? null,
    LanguageId: getCurrentLanguageId()
  }),
  [filters, getCurrentLanguageId]
);


  return {
    rows, loading, totalCount, pagination, filters,
    filterFields, customFilterTypes, customFilterOptions,
    handleSearch, handleFilterChange, handleResetFilters, handleApplyFilters,
    onPageChange, exportParams,

    logDialogVisible, selectedPartCode,
    logRows, logLoading: isLogLoading || isLogFetching, logTotalCount,
    logPagination, openLogDialog, closeLogDialog, onLogPageChange
  };
};

export default useDealerStockReportData;
